import React from "react";
import { Box, Container, Paper, Typography, Stack } from "@mui/material";
import { ChevronRight, AutoAwesome } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const prefix = process.env.REACT_APP_STUDENT_PREFIX || "student";

const WIZARD_OPTIONS = [
  {
    id: "ai-tutoring",
    path: `/${prefix}/wizard/ai-tutoring`,
    tint: "#1D4ED8",
    title: "AI Tutoring",
    subtitle: "Scan a tricky question for step-by-step help",
  },
  {
    id: "ai-marking",
    path: `/${prefix}/wizard/ai-marking`,
    tint: "#9F1239",
    title: "AI Marking",
    subtitle: "Scan and mark practice papers instantly",
  },
  {
    id: "writing-coach",
    path: `/${prefix}/wizard/writing-coach`,
    tint: "#047857",
    title: "Writing Coach",
    subtitle: "Scan your creative writing for expert feedback",
  },
  {
    id: "daily-word-challenge",
    path: `/${prefix}/wizard/daily-word-challenge`,
    tint: "#D97706",
    title: "Daily Word Challenge",
    subtitle: "Learn a new word every day and test yourself",
  },
];

/**
 * Student Wizard hub — mirrors the mobile app Wizard tab (mobile users only).
 */
export default function WizardHub() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: "100%",
        background: "linear-gradient(180deg, #F8FAFC 0%, #EEF2FF 100%)",
        py: { xs: 2, md: 3 },
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 3 }}>
          <Box
            sx={{
              width: 4,
              alignSelf: "stretch",
              borderRadius: 2,
              background: "linear-gradient(180deg, #6366F1 0%, #4F46E5 100%)",
              minHeight: 48,
            }}
          />
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AutoAwesome sx={{ color: "#4F46E5", fontSize: 28 }} />
              <Typography variant="h4" sx={{ fontWeight: 800, color: "#0F172A", letterSpacing: -0.4 }}>
                Wizard
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ color: "#64748B", mt: 0.5 }}>
              AI tools to scan, learn, and improve
            </Typography>
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, md: 2 },
            borderRadius: 3,
            border: "1px solid #E2E8F0",
            bgcolor: "#fff",
          }}
        >
          <Stack spacing={1.5}>
            {WIZARD_OPTIONS.map((option) => (
              <Box
                key={option.id}
                component="button"
                type="button"
                onClick={() => navigate(option.path)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  width: "100%",
                  textAlign: "left",
                  cursor: "pointer",
                  border: `1px solid ${option.tint}44`,
                  bgcolor: `${option.tint}12`,
                  borderRadius: 2.5,
                  px: 2,
                  py: 1.75,
                  transition: "transform 0.15s ease, box-shadow 0.15s ease",
                  font: "inherit",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: `0 6px 16px ${option.tint}22`,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: option.tint,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, color: "#0F172A", fontSize: "1.05rem" }}>
                    {option.title}
                  </Typography>
                  <Typography sx={{ color: "#64748B", fontSize: "0.9rem", mt: 0.25 }}>
                    {option.subtitle}
                  </Typography>
                </Box>
                <ChevronRight sx={{ color: "#94A3B8" }} />
              </Box>
            ))}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
